import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, { buildFaqSchema, buildLocalBusinessSchema } from "@/components/SeoLandingPage";
import { buildBreadcrumbSchema, ldScript, pageHead, SITE_URL } from "@/lib/page-schema";
import heroImage from "@/assets/sprinter-starlight-red.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";

const PATH = "/corporate-holiday-party-transportation-atlanta";
const TITLE = "Corporate Holiday Party Transportation Atlanta | Executive Sprinters";
const DESC =
  "Holiday party transportation for Atlanta companies: guest loops between office, hotel and venue, executive arrivals, and safe rides home. One point of contact. Our own fleet and chauffeurs.";

const faqs = [
  {
    q: "When should we book holiday party transportation?",
    a: "As early as you have a date. December weekends are the first dates to go. Send your date now and we'll tell you what's still open.",
  },
  {
    q: "What do you need from us to plan it?",
    a: "Your date, the venue, your headcount and the loop schedule: where guests start, when the first and last runs should leave, and when the party ends.",
  },
  {
    q: "Can you get everyone home safely after the party?",
    a: "Yes. We plan staggered return runs after the party so guests get home safely, with our own background-checked, fingerprinted chauffeurs.",
  },
];

export const Route = createFileRoute("/corporate-holiday-party-transportation-atlanta")({
  head: () =>
    pageHead({
      path: PATH,
      title: TITLE,
      description: DESC,
      scripts: [
        ldScript(
          buildLocalBusinessSchema({
            url: `${SITE_URL}${PATH}`,
            name: "Atlanta Luxury Sprinters, Corporate Holiday Party Transportation",
            image: `${SITE_URL}/favicon.ico`,
            description: DESC,
            serviceType: "Corporate Event Transportation",
            areaServed: [{ type: "City", name: "Atlanta" }, { type: "City", name: "Buckhead" }, { type: "City", name: "Midtown" }],
          }),
        ),
        ldScript(buildFaqSchema(faqs)),
        ldScript(
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Corporate Holiday Party Transportation", path: PATH },
          ]),
        ),
      ],
    }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Holiday Parties • Atlanta"
      h1="Corporate Holiday Party Transportation in Atlanta"
      intro="You have the venue, menu and guest list to manage. Let me handle office and hotel trips, executive arrivals and the ride home, with one point of contact."
      hideQuotePricing
      showCorporateForm
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter cabin with starlight ceiling ready for a corporate holiday party"
      midImage={midImage}
      midAlt="Executive Style Sprinter for corporate holiday party arrivals in Atlanta"
      sectionOneTitle="Let Me Handle the Travel"
      sectionOneBody={[
        "Text me your date, venue and headcount, and I'll check what's available.",
        "We own every vehicle in our fleet. My chauffeurs handle guest trips and executive arrivals, with return runs planned before the evening begins.",
      ]}
      bulletsTitle="What We Cover"
      bulletsIntro="Arrivals, return trips and one contact throughout."
      bullets={[
        "Guest loops between the office or hotel and the venue",
        "Executive arrivals in an Executive Style Sprinter or Suburban SUV",
        "Getting everyone home safely after the party",
        "One point of contact from planning to the last ride",
        "Background-checked, fingerprinted, long-tenured chauffeurs",
      ]}
      coverageTitle="Where Atlanta Holiday Parties Happen"
      coverageIntro="Offices, hotels and venues across the metro."
      coverage={["Buckhead", "Midtown", "Downtown Atlanta", "Sandy Springs", "Alpharetta", "Perimeter Center"]}
      detailSections={[
        {
          title: "Your Planning Timeline",
          list: [
            "As soon as you have a date: send it to us so we can hold a vehicle.",
            "Once the venue is set: send the venue address and your expected headcount.",
            "Two weeks out: confirm the loop schedule and any executive arrivals.",
            "Week of: final headcount and pickup points.",
            "Night of: your single point of contact is reachable the whole evening.",
          ],
        },
        {
          title: "What to Send Us",
          list: ["Date", "Venue", "Headcount", "Loop schedule: pickup points, first and last run times, and when the party ends"],
        },
        {
          title: "Corporate Accounts",
          body: [
            "Booking travel for your executives all year? A corporate account gives you one contact and monthly invoicing. See Corporate Accounts and Corporate Transportation Atlanta, or use the form below.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/corporate-accounts", title: "Corporate Accounts", desc: "One contact for every executive trip." },
        { to: "/corporate-transportation-atlanta", title: "Corporate Transportation Atlanta", desc: "Dedicated corporate transportation." },
        { to: "/blog/corporate-holiday-party-transportation-planning-guide", title: "Holiday Party Planning Guide", desc: "A timeline for executive assistants." },
      ]}
      ctaTitle="Text Me Your Holiday Party Date"
      ctaBody="Send me the date, venue, headcount and pickup points. I'll check availability myself and send your quote in writing."
    />
  );
}
